import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import CustomPagination from "components/common/CustomPagination"
import DataTable from "ui/DataTable"
import IconWithBg from "ui/IconWithBg"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { routes } from "router"
import { Card, Flex, Empty, Button, Form, message, Select } from "antd"
import { LeftOutlined, EditOutlined } from "@ant-design/icons"
import ImportErrorForm from "../ClientTable/Modals/ImportReport/ImportErrorForm"
import { Text, Title } from "ui/Typography"
import SearchInput from "ui/SearchInput"
import { ImportsNotFound } from "ui/Icon/DSIcon/ImportsNotFound"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import styles from "../ViewImportRecords/viewImport.module.css"
import { getHeaders, renderImportStatusTag } from "./headers"
import ReportCard from "./ReportCard"
import { useDevice } from "hooks/useDevice"
import DataTableResponsive from "ui/DataTableResponsive"
import { getImportClientDetail, updateExisting } from "store/clients"
import Spinner from "components/common/Spinner"
import ImportClientList from "../ClientTable/Modals/ImportClientList"
import { serialize } from "object-to-formdata"
import DuplicateRecordsSection from "../ClientTable/Modals/ImportReport/DuplicateRecordsSection"
import CustomDrawer from "ui/Drawer"

const extraRowsResolver = (row, isMobile, setMobileRecord, setOpenMobileEdit, form, t) => {
  const editable =
    row?.current_status === "partial_completed" && (row?.import_status === "failed" || row?.import_status === "action_required")
  if (isMobile && editable) {
    return (
      <Button
        type="primary"
        ghost
        shape="round"
        block
        onClick={(e) => {
          e?.stopPropagation?.()
          setMobileRecord(row)
          setOpenMobileEdit(true)
          form.resetFields()
        }}
      >
        {t("global.edit_data", { defaultValue: "Edit Data" })}
      </Button>
    )
  }
  return null
}

const ClientImportDetail = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const { importClientDetailData } = useSelector((state) => state.clients)
  const [sortKey, setSortKey] = useState("")
  const [sortOrder, setSortOrder] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [status, setStatus] = useState("")
  const [openImportClientList, setOpenImportClientList] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [openMobileEdit, setOpenMobileEdit] = useState(false)
  const [mobileRecord, setMobileRecord] = useState(null)
  const [showAllMobile, setShowAllMobile] = useState(false)
  const [form] = Form.useForm()
  const { isTablet, isMobile } = useDevice()

  const importDetailInfo = importClientDetailData?.data
  const loading = importClientDetailData?.loading
  const updateExistingLoading = useSelector((state) => state.clients.importClientData.loading)
  const getErrorMessage = (item) => {
    if (!item.message) return item.error_message || ""

    // If message is a string
    if (typeof item.message === "string") return item.message

    // If message is an object (e.g., {email: "error", gender: "required"})
    if (typeof item.message === "object") {
      const errors = Object.values(item.message).join(", ")
      return errors || ""
    }

    return ""
  }
  const actionIconResolver = (row, isTablet) => {
    const editable =
      row?.current_status === "partial_completed" && (row?.import_status === "failed" || row?.import_status === "action_required")
    if (isTablet && editable) {
      return <EditOutlined style={{ fontSize: 18, color: "#595959" }} />
    }
    return null
  }

  // Convert all records to array grouped by status in specific order
  const allFailedRecords = React.useMemo(() => {
    const records = []

    // Helper function to process records with a specific status
    const processRecords = (result, recordObj, status) => {
      if (!recordObj) return

      for (const [key, item] of Object.entries(recordObj)) {
        const firstName = item.row?.first_name || ""
        const lastName = item.row?.last_name || ""
        const clientName = [firstName, lastName].filter(Boolean).join(" ") || "-"

        records.push({
          key: `${status}_${key}`,
          row_number: item.row?.row_number || key,
          client_name: clientName,
          first_name: item.row?.first_name || "",
          last_name: item.row?.last_name || "",
          email: item.row?.email || "",
          phone_number: item.row?.phone_number || "",
          gender: item.row?.gender || "",
          birthday: item.row?.birthday || "",
          country_code: item.row?.country_code || "",
          error_message: getErrorMessage(item),
          raw_message: item.message,
          import_status: status,
          old_row: item.old_row,
          current_status: result?.current_status
        })
      }
    }

    // Process records in the specified order
    processRecords(importDetailInfo, importDetailInfo?.result?.failed_records, "failed")
    processRecords(importDetailInfo, importDetailInfo?.result?.duplicate_records, "action_required")
    processRecords(importDetailInfo, importDetailInfo?.result?.success_records, "completed")

    return records
  }, [importDetailInfo?.result])

  // Filter and sort records based on search term, status, and sorting
  const filteredRecords = React.useMemo(() => {
    let records = [...allFailedRecords]
    // Filter by search term (first name, last name, full name)
    if (search) {
      const lowerSearchTerm = search.toLowerCase().trim()
      records = records.filter((record) => {
        const firstName = (record.client_name.split(" ")[0] || "").toLowerCase()
        const lastName = (record.client_name.split(" ").slice(1).join(" ") || "").toLowerCase()
        const fullName = record.client_name.toLowerCase()

        return firstName.includes(lowerSearchTerm) || lastName.includes(lowerSearchTerm) || fullName.includes(lowerSearchTerm)
      })
    }

    // Filter by status
    if (status) {
      records = records.filter((record) => record.import_status === status)
    }

    // Apply sorting
    if (sortKey && sortOrder) {
      records.sort((a, b) => {
        let aValue = a[sortKey]
        let bValue = b[sortKey]

        // Handle client_name sorting (case-insensitive)
        if (sortKey === "client_name") {
          aValue = (aValue || "").toLowerCase()
          bValue = (bValue || "").toLowerCase()
        }

        // Handle import_status sorting (custom order: failed > action_required > completed)
        if (sortKey === "import_status") {
          const statusOrder = { failed: 0, action_required: 1, completed: 2 }
          aValue = statusOrder[aValue] ?? 999
          bValue = statusOrder[bValue] ?? 999
        }

        // Handle row_number sorting (numeric)
        if (sortKey === "row_number") {
          aValue = Number.parseInt(aValue) || 0
          bValue = Number.parseInt(bValue) || 0
        }

        // Compare values
        if (aValue < bValue) return sortOrder === "ascend" ? -1 : 1
        if (aValue > bValue) return sortOrder === "ascend" ? 1 : -1
        return 0
      })
    }

    return records
  }, [allFailedRecords, search, status, sortKey, sortOrder])

  // Calculate pagination client-side
  const totalRecords = filteredRecords.length
  const startIndex = (page - 1) * pageSize
  const endIndex = startIndex + pageSize

  // Get paginated data for current page
  const dataSource = React.useMemo(() => {
    return filteredRecords.slice(startIndex, endIndex)
  }, [filteredRecords, startIndex, endIndex])

  // Create custom metadata for pagination component
  const metadata = React.useMemo(() => {
    const totalPages = Math.ceil(totalRecords / pageSize) || 1
    return {
      total_results: totalRecords,
      page: page,
      per_page: pageSize,
      total_pages: totalPages,
      next_page: page < totalPages ? page + 1 : null,
      prev_page: page > 1 ? page - 1 : null
    }
  }, [totalRecords, page, pageSize])

  const onSortChange = (_pagination, _filters, sorter) => {
    // Ant Design Table onChange passes (pagination, filters, sorter, extra)
    // sorter contains: { field, order, column, columnKey }
    setSortKey(sorter.field || sorter.columnKey || "")
    setSortOrder(sorter.order || "")
  }

  const handleBackToHistory = () => {
    navigate(routes.guideImportClientHistory())
  }

  useEffect(() => {
    if (id) {
      dispatch(getImportClientDetail(id))
    }
  }, [dispatch, id])

  const handleStatusChange = (value) => {
    setStatus(value || "")
    setPage(1)
  }

  const handleSearchChange = (value) => {
    setSearch(value)
    setPage(1)
  }

  const handleOpenImportModal = () => {
    setOpenImportClientList(true)
  }

  const handleEditRecord = useCallback(
    (record) => {
      const key = record.key
      setExpandedRowKeys((prev) => {
        const isExpanded = prev.includes(key)
        if (isExpanded) {
          // Close the row
          return prev.filter((k) => k !== key)
        } else {
          // Open the row (close others and open this one)
          // Reset form with current record data
          form.resetFields()
          return [key]
        }
      })
    },
    [form]
  )

  // Build columns for desktop and mapping for mobile labels
  const columns = React.useMemo(
    () =>
      getHeaders({
        field: sortKey,
        order: sortOrder,
        onEditClick: handleEditRecord
      }),
    [sortKey, sortOrder, handleEditRecord]
  )

  const { labelMap, fieldOrder } = React.useMemo(() => {
    const map = {}
    const order = []
    for (const col of columns) {
      const key = col.dataIndex || col.key
      if (!key) continue
      // On tablet/mobile, do not include actions in the field list; on desktop keep as usual
      if ((isMobile || isTablet) && key === "actions") continue
      // On tablet/mobile, show a display tag for import_status while preserving raw value on the row
      if ((isMobile || isTablet) && key === "import_status") {
        order.push("import_status_display")
        map["import_status_display"] = col.title
        continue
      }
      order.push(key)
      map[key] = col.title
    }
    return { labelMap: map, fieldOrder: order }
  }, [columns, isMobile, isTablet])

  // Prepare data for responsive list (no actions injected; actions are rendered via resolvers)
  const mobileDataSource = React.useMemo(() => {
    const mobileRows = showAllMobile ? filteredRecords : dataSource
    return (mobileRows || []).map((r) => ({
      ...r,
      import_status_display: renderImportStatusTag(r.import_status)
    }))
  }, [showAllMobile, filteredRecords, dataSource])

  useEffect(() => {
    setShowAllMobile(false)
  }, [search, status, sortKey, sortOrder])

  const handleUpdateExisting = (record) => {
    const formValues = form.getFieldsValue()
    const guide_client = {
      ...formValues,
      row: record.row_number,
      first_name: formValues.first_name || record.first_name,
      last_name: formValues.last_name || record.last_name,
      email: formValues.email || record.email,
      phone_number: formValues.phone_number || record.phone_number,
      gender: formValues.gender || record.gender,
      birthday: formValues.birthday || record.birthday,
      country_code: formValues.country_code || record.country_code,
      failed: true
    }
    const formData = serialize({ guide_client }, { indices: true })

    dispatch(updateExisting(formData, id))
      .then((res) => {
        message.success(res?.message)
        setExpandedRowKeys([])
        form.resetFields()
        // Update the local state to remove the updated record
        dispatch(getImportClientDetail(id))
      })
      .catch((error) => {
        console.error("Error updating client:", error)
      })
  }

  const expandedRowRender = (record) => {
    // Get error fields from the raw_message object
    const errorMessages = record.raw_message || {}
    const errorFields = typeof errorMessages === "object" ? Object.keys(errorMessages) : []

    // Prepare row data for the form
    const rowData = {
      first_name: record.first_name,
      last_name: record.last_name,
      email: record.email,
      phone_number: record.phone_number,
      gender: record.gender,
      birthday: record.birthday
    }
    // Get the duplicate records for the current row
    const oldRowData = importDetailInfo?.result?.duplicate_records?.[record.row_number] || []

    if (record.import_status === "action_required" && Object.keys(oldRowData).length > 0) {
      return (
        <div style={{ padding: "16px 24px", backgroundColor: "#fafafa" }}>
          <Flex align="center" gap={8}>
            <Title level={5} style={{ margin: 0 }}>
              {record.client_name}
            </Title>
          </Flex>
          <DuplicateRecordsSection
            duplicateCount={1}
            duplicateRecords={{ [record.row_number]: oldRowData }}
            importId={id}
            t={t}
            onClose={() => {
              setExpandedRowKeys([])
              form.resetFields()
            }}
          />
        </div>
      )
    }

    if (record.import_status === "failed") {
      return (
        <div style={{ padding: "16px 24px", backgroundColor: "#fafafa" }}>
          <Flex vertical gap={16}>
            <Flex align="center" gap={8}>
              <Title level={5} style={{ margin: 0 }}>
                {record.client_name}
              </Title>
            </Flex>
            <>
              <Text type="secondary">{t("clients.import_report.provide_phone_or_email")}</Text>
              <ImportErrorForm form={form} rowData={rowData} errorFields={errorFields} errorMessages={errorMessages} />
              <Flex justify="flex-end" gap={8}>
                <Button
                  onClick={() => {
                    setExpandedRowKeys([])
                    form.resetFields()
                  }}
                >
                  {t("global.cancel")}
                </Button>
                <Button
                  loading={updateExistingLoading}
                  disabled={updateExistingLoading}
                  type="primary"
                  onClick={() => handleUpdateExisting(record)}
                >
                  {t("global.save")}
                </Button>
              </Flex>
            </>
          </Flex>
        </div>
      )
    }
  }

  return (
    <div>
      <Flex gap={16}>
        <IconWithBg icon={<LeftOutlined />} onClick={handleBackToHistory} />
        <Title level={3}>{t("clients.client_import_details")}</Title>
      </Flex>
      <ReportCard data={importDetailInfo} onReupload={handleOpenImportModal} />

      <Card>
        <Title level={5}>{t("clients.details")}</Title>
        <Flex gap={16} className="w-full mt-m mb-m" wrap="wrap">
          <SearchInput
            className={styles.clientSearchInput}
            searchBy={search}
            setSearchBy={handleSearchChange}
            ariaLabel={t("clients.aria.search", { name: "client-history" })}
            ariaControls={t("clients.aria.search_result", { name: "client-history" })}
            placeholder={t("global.search")}
          />
          <Select
            placeholder="Select Status"
            style={{ width: 200 }}
            allowClear
            value={status || undefined}
            onChange={(value) => {
              handleStatusChange(value)
            }}
            options={[
              { value: "failed", label: "Failed" },
              { value: "action_required", label: "Action Required" },
              { value: "completed", label: "Completed" }
            ]}
            size="large"
          />
        </Flex>
        <Flex vertical gap={16}>
          <WithEmptyStateWrapper
            name={t("clients.client_import_details")}
            hasItems={dataSource?.length > 0}
            fallbackTitle={"abc"}
            fallback={
              loading ? (
                <Spinner />
              ) : (
                <div className={styles.contentWrapper}>
                  <Empty
                    image={<ImportsNotFound />}
                    description={
                      <>
                        <Text strong size="lg">
                          {"No client details found"}
                        </Text>
                        <p style={{ color: "#888" }}>{"No clients found for this import."}</p>
                      </>
                    }
                  />
                </div>
              )
            }
          >
            {isTablet || isMobile ? (
              <>
                <DataTableResponsive
                  data={mobileDataSource || []}
                  labelMap={labelMap}
                  fieldOrder={fieldOrder}
                  fullsizeColumnOnMobile={true}
                  actionIconResolver={(row) => actionIconResolver(row, isTablet)}
                  onActionIconClick={(row) => {
                    setMobileRecord(row)
                    setOpenMobileEdit(true)
                    form.resetFields()
                  }}
                  extraRowsResolver={(row) => extraRowsResolver(row, isMobile, setMobileRecord, setOpenMobileEdit, form, t)}
                />
                {!showAllMobile && filteredRecords.length > (dataSource?.length || 0) ? (
                  <div className="mt-m text-center">
                    <Button type="primary" onClick={() => setShowAllMobile(true)}>
                      {t("global.see_more", { defaultValue: "See more" })}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="overflow-auto">
                <DataTable
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  onChange={onSortChange}
                  rowSelection={false}
                  rowKey={(record) => record.key}
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: (_expanded, record) => handleEditRecord(record),
                    expandIcon: () => null // Hide default expand icon since we're using the edit button
                  }}
                />
                {metadata && metadata?.total_results > 0 && (
                  <div className="mb-m mt-m">
                    <CustomPagination
                      metadata={metadata}
                      pageSize={pageSize}
                      total={metadata?.total_results || 0}
                      onPageChange={setPage}
                      onPageSizeChange={(newSize) => {
                        setPageSize(newSize)
                        setPage(1)
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </WithEmptyStateWrapper>
        </Flex>
      </Card>
      <ImportClientList open={openImportClientList} onClose={() => setOpenImportClientList(false)} onReuploadId={id} />
      {/* Mobile/Tablet Edit Drawer */}
      <CustomDrawer open={openMobileEdit} setOpen={setOpenMobileEdit} title={mobileRecord?.client_name || "Edit"}>
        {mobileRecord ? (
          <div style={{ padding: 8 }}>
            {mobileRecord.import_status === "action_required" ? (
              <DuplicateRecordsSection
                duplicateCount={1}
                duplicateRecords={{
                  [mobileRecord.row_number]: importDetailInfo?.result?.duplicate_records?.[mobileRecord.row_number] || []
                }}
                importId={id}
                t={t}
                onClose={() => {
                  setOpenMobileEdit(false)
                  form.resetFields()
                }}
              />
            ) : null}

            {mobileRecord.import_status === "failed" ? (
              <>
                <Text type="secondary">{t("clients.import_report.provide_phone_or_email")}</Text>
                {(() => {
                  const errorMessages = mobileRecord.raw_message || {}
                  const errorFields = typeof errorMessages === "object" ? Object.keys(errorMessages) : []
                  const rowData = {
                    first_name: mobileRecord.first_name,
                    last_name: mobileRecord.last_name,
                    email: mobileRecord.email,
                    phone_number: mobileRecord.phone_number,
                    gender: mobileRecord.gender,
                    birthday: mobileRecord.birthday
                  }
                  return <ImportErrorForm form={form} rowData={rowData} errorFields={errorFields} errorMessages={errorMessages} />
                })()}
                <Flex justify="flex-end" gap={8} className="mt-m">
                  <Button
                    onClick={() => {
                      setOpenMobileEdit(false)
                      form.resetFields()
                    }}
                  >
                    {t("global.cancel")}
                  </Button>
                  <Button type="primary" onClick={() => handleUpdateExisting(mobileRecord)}>
                    {t("global.save")}
                  </Button>
                </Flex>
              </>
            ) : null}
          </div>
        ) : null}
      </CustomDrawer>
    </div>
  )
}

export default ClientImportDetail
